import { FormArray, FormControl, FormGroup } from "@angular/forms";

// ============================================================
// API Response Models
// ============================================================

/**
 * Survey list item response from GET /v1/survey/{siteId}
 */
export interface SurveyListResponse {
  errors: string[];
  result: {
    surveys: SurveyListItem[];
  };
}

/**
 * Individual survey in list response
 */
export interface SurveyListItem {
  survey: Survey;
  hasCustomerAnswer: boolean;
  createdDate: string;
  firstResponseDate: string | null;
  lastResponseDate: string | null;
}

/**
 * Single survey response from GET /v1/survey/{siteId}/{id}
 */
export interface SurveyByIdResponse {
  errors: string[];
  survey: Survey;
  hasCustomerAnswer: boolean;
}

/**
 * Core Survey model
 */
export interface Survey {
  id: string;
  siteId: number;
  name: string;
  question: string;
  actionTrigger: string; // stored as string, but represents number
  isEnabled: boolean;
  isDeleted?: boolean;
  answers: SurveyAnswer[];
  createdDate?: string;
  firstResponseDate?: string | null;
  lastResponseDate?: string | null;
}

/**
 * Survey answer with connected offer
 */
export interface SurveyAnswer {
  id?: string | null;
  answer: string;
  type: SurveyAnswerType;
  offerApplicableItem: OfferApplicableItem | null;
}

/**
 * Answer type enum
 * 0 = No comment, 1 = Required comment, 2 = Optional comment
 */
export enum SurveyAnswerType {
  NoComment = 0,
  RequiredComment = 1,
  OptionalComment = 2,
}

/**
 * Offer configuration attached to an answer
 */
export interface OfferApplicableItem {
  id?: string | null;
  offerId?: string;
  itemId?: string | null;
  itemType?: OfferType | null;
  phrase?: string | null;
  tooltip?: string;
  quantity?: number | null;
  maxCycles?: number | null;
}

/**
 * Offer type enum
 */
export enum OfferType {
  FreeProduct = 0,
  Discount = 1,
  Snooze = 2,
  ChangeFrequency = 3,
  NoOffer = 4,
  SkipCycle = 5,
}

/**
 * Predefined answer response
 */
export interface PredefinedAnswer {
  id: string;
  answer: string;
  siteId: number;
}

// ============================================================
// Request Models
// ============================================================

/**
 * Create survey request body
 */
export interface CreateSurveyRequest {
  siteId: number;
  name: string;
  question: string;
  actionTrigger: number[];
  answers: SurveyAnswer[];
  isEnabled: boolean;
}

/**
 * Update survey request body
 */
export interface UpdateSurveyRequest {
  id: number;
  siteId: number;
  name: string;
  question: string;
  answers: UpdateSurveyAnswerDto[];
}

/**
 * Answer DTO for update request (simplified)
 */
export interface UpdateSurveyAnswerDto {
  id: string;
  answer: string;
  offerItemPhrase?: string;
}

/**
 * Enable/disable and duplicate responses
 */
export interface SurveyActionResponse {
  warning?: string;
  errors?: string[];
  newSurveyId?: string;
}

// ============================================================
// Filter Models
// ============================================================

/**
 * Survey filter query parameters
 */
export interface SurveyFilterParams {
  Enabled?: boolean;
  Deleted?: boolean;
  ActionTrigger?: number[];
}

/**
 * Filter form controls
 */
export interface SurveyFilterControls {
  status: FormControl<string[]>;
  actionTrigger: FormControl<number[]>;
}

// ============================================================
// Form Models
// ============================================================

/**
 * Offer applicable item form group type
 */
export interface OfferApplicableItemFormControls {
  id: FormControl<string | null>;
  itemId: FormControl<string | null>;
  itemType: FormControl<OfferType | null>;
  maxCycles: FormControl<number | null>;
  phrase: FormControl<string | null>;
  quantity: FormControl<number | null>;
}

/**
 * Survey answer form group type
 */
export interface SurveyAnswerFormControls {
  id: FormControl<string | null>;
  answer: FormControl<string>;
  type: FormControl<SurveyAnswerType>;
  enableComment: FormControl<boolean>;
  requireComment: FormControl<boolean>;
  offerApplicableItem: FormGroup<OfferApplicableItemFormControls>;
}

/**
 * Main survey form group type
 */
export interface SurveyFormControls {
  actionTrigger: FormControl<number | null>;
  name: FormControl<string>;
  question: FormControl<string>;
  answers: FormArray<FormGroup<SurveyAnswerFormControls>>;
}

// ============================================================
// UI Options
// ============================================================

/**
 * Dropdown option type
 */
export interface EnumOption {
  displayName: string;
  value: string | number;
  icon?: string;
}

/**
 * Action trigger options
 */
export const ACTION_TRIGGER_OPTIONS: EnumOption[] = [
  { displayName: "Pause", value: 0 },
  { displayName: "Cancel", value: 3 },
];

/**
 * Offer type options
 */
export const OFFER_TYPE_OPTIONS: EnumOption[] = [
  { displayName: "No Offer", value: OfferType.NoOffer, icon: "pi pi-ban" },
  {
    displayName: "Offer Free Product",
    value: OfferType.FreeProduct,
    icon: "pi pi-gift",
  },
  {
    displayName: "Offer Discount",
    value: OfferType.Discount,
    icon: "pi pi-percentage",
  },
  {
    displayName: "Offer Change Frequency",
    value: OfferType.ChangeFrequency,
    icon: "pi pi-sync",
  },
  {
    displayName: "Offer Skip Cycle",
    value: OfferType.SkipCycle,
    icon: "pi pi-forward",
  },
];

/**
 * Max cycles options (1-5)
 * Note: Values are numbers to match form control types
 */
export const MAX_CYCLES_OPTIONS: EnumOption[] = [
  { displayName: "1 Cycle", value: 1 },
  { displayName: "2 Cycles", value: 2 },
  { displayName: "3 Cycles", value: 3 },
  { displayName: "4 Cycles", value: 4 },
  { displayName: "5 Cycles", value: 5 },
];

/**
 * Quantity options (1-5)
 * Note: Values are numbers to match form control types
 */
export const QUANTITY_OPTIONS: EnumOption[] = [
  { displayName: "1 (One)", value: 1 },
  { displayName: "2 (Two)", value: 2 },
  { displayName: "3 (Three)", value: 3 },
  { displayName: "4 (Four)", value: 4 },
  { displayName: "5 (Five)", value: 5 },
];

/**
 * Default answers for Pause trigger
 */
export const DEFAULT_PAUSE_ANSWERS: Partial<SurveyAnswer>[] = [
  { answer: "I have too much product", type: SurveyAnswerType.NoComment },
  { answer: "I can't afford it right now", type: SurveyAnswerType.NoComment },
  {
    answer: "I don't need this product right now",
    type: SurveyAnswerType.NoComment,
  },
  {
    answer: "I will be traveling or unavailable",
    type: SurveyAnswerType.NoComment,
  },
];

/**
 * Default answers for Cancel trigger
 */
export const DEFAULT_CANCEL_ANSWERS: Partial<SurveyAnswer>[] = [
  { answer: "I have too much product", type: SurveyAnswerType.NoComment },
  { answer: "It is too expensive", type: SurveyAnswerType.NoComment },
  { answer: "I no longer use this product", type: SurveyAnswerType.NoComment },
  { answer: "Product Issues", type: SurveyAnswerType.NoComment },
];

/**
 * Dynamic field placeholders for offer text
 */
export const DYNAMIC_FIELDS = [
  {
    variable: "{{Quantity}}",
    description: "The quantity selected for the free product offer",
  },
  { variable: "{{ProductName}}", description: "The name of the free product" },
  { variable: "{{Price}}", description: "The price of the product" },
  {
    variable: "{{DiscountMaxCycles}}",
    description: "Maximum cycles the discount applies",
  },
  {
    variable: "{{FreeProductMaxCycles}}",
    description: "Maximum cycles for free product",
  },
  {
    variable: "{{CouponAmount}}",
    description: "The discount amount from the coupon",
  },
];

/**
 * Default offer text templates
 */
export const DEFAULT_OFFER_TEXT = {
  freeProduct:
    "Receive {{Quantity}} {{ProductName}} for FREE on your next order!",
  discount:
    "Receive {{CouponAmount}} off your next {{DiscountMaxCycles}} cycle!",
};
